import React, { useEffect, useState } from "react";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Form,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import {
  withRouter,
  Link,
  useHistory,
  RouteComponentProps,
} from "react-router-dom";
import { InputType } from "reactstrap/types/lib/Input";
import { useTranslation } from "react-i18next";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useSigninMutation } from "../../store/api/authApi";
import { setCredentials } from "../../store/slices/authSlice";
import { ParticlesAuth } from "../AuthenticationInner";
import { ParsedError, SigninRequest } from "../../interfaces";
import { flattenErrorMessage, getServerError } from "../../services";
import { ActivityButton, AppLogo } from "../../components/Common";
import artboard from "../../assets/images/Artboard.png";
import LanguageDropdown from "../../components/Common/LanguageDropdown";
import { ErrorHandlingTypeEnum } from "../../enums";

type TProps = RouteComponentProps & {};
const LoginPageComponent: React.FC<TProps> = (props: TProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  document.title = `${t("documentTitle", { pageName: "login" })}`;
  const [passwordType, setPasswordType] = useState<InputType>("password");
  const [loginError, setLoginError] = useState("");

  const [
    signin,
    {
      isLoading: isLoadingSignin,
      isError: isErrorSignin,
      isSuccess: isSuccessSignin,
      data: dataSignin,
      error: errorSignin,
    },
  ] = useSigninMutation();

  const [signinRequest] = useState<SigninRequest>({
    countryCode: process.env.REACT_APP_COUNTRY_CODE || "965",
    phoneNumber: "",
    password: "",
  });

  // #region form
  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      countryCode: signinRequest.countryCode,
      phoneNumber: signinRequest.phoneNumber,
      password: signinRequest.password,
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string().required(
        t("isNotEmpty", {
          displayName: "phoneNumber",
        }),
      ),
      password: Yup.string().required(
        t("isNotEmpty", { displayName: "password" }),
      ),
    }),
    onSubmit: async (values) => {
      setLoginError("");
      try {
        await signin(values).unwrap();
      } catch (error) {
        const maybeParsed = error as ParsedError;
        const parsed =
          maybeParsed?.type === ErrorHandlingTypeEnum.FORM ||
          maybeParsed?.type === ErrorHandlingTypeEnum.ALERT ||
          maybeParsed?.type === ErrorHandlingTypeEnum.UNKNOWN
            ? maybeParsed
            : getServerError(error as FetchBaseQueryError);

        if (
          parsed.type === ErrorHandlingTypeEnum.FORM &&
          parsed.error &&
          typeof parsed.error === "object"
        ) {
          form.setErrors(parsed.error);
          return;
        }

        setLoginError(
          flattenErrorMessage(parsed.error) || t("invalidPhoneOrPassword"),
        );
      }
    },
  });
  //#endregion

  useEffect(() => {
    if (isLoadingSignin || !isErrorSignin || !errorSignin) {
      return;
    }

    const maybeParsed = errorSignin as ParsedError;
    const alreadyToasted =
      maybeParsed?.type === ErrorHandlingTypeEnum.ALERT ||
      maybeParsed?.type === ErrorHandlingTypeEnum.UNKNOWN;
    const parsed =
      alreadyToasted || maybeParsed?.type === ErrorHandlingTypeEnum.FORM
        ? maybeParsed
        : getServerError(errorSignin as FetchBaseQueryError);

    if (
      parsed.type === ErrorHandlingTypeEnum.FORM &&
      parsed.error &&
      typeof parsed.error === "object"
    ) {
      form.setErrors(parsed.error);
      setLoginError("");
      return;
    }

    const message =
      flattenErrorMessage(parsed.error) || t("invalidPhoneOrPassword");
    setLoginError(message);
    if (!alreadyToasted) {
      toast.error(message, { toastId: "login-error" });
    }
  }, [isLoadingSignin, isErrorSignin, errorSignin, t]);

  useEffect(() => {
    if (!isLoadingSignin && isSuccessSignin && dataSignin) {
      if (dataSignin.phoneVerificationRequired) {
      } else {
        dispatch(
          setCredentials({
            token: dataSignin.token,
            refreshToken: dataSignin.refreshToken,
          }),
        );
        history.replace("/");
      }
    }
  }, [isLoadingSignin, isSuccessSignin, dataSignin, dispatch, history]);

  /**
   * Password show hide
   */
  const handleClickShowPassword = () => {
    if (passwordType === "text") setPasswordType("password");
    else setPasswordType("text");
  };

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row className="justify-content-center ">
              <Col>
                <Card className="custom-radius">
                  <CardBody>
                    <Row>
                      <Col
                        md={6}
                        lg={6}
                        xl={6}
                        className="d-none d-md-block align-self-center "
                      >
                        <img src={artboard} alt="" width="100%" />
                      </Col>
                      <Col md={6} lg={6} xl={6} className="padding mt-3">
                        <div className="d-flex justify-content-center">
                          <AppLogo />
                        </div>
                        <div className="p-2 mt-4">
                          <div className="justify-content-center align-items-center d-flex">
                            <LanguageDropdown />
                          </div>
                          <Form
                            onSubmit={(e) => {
                              e.preventDefault();
                              form.handleSubmit();
                              return false;
                            }}
                            action="#"
                          >
                            {loginError ? (
                              <Alert color="danger" className="mb-3">
                                {loginError}
                              </Alert>
                            ) : null}
                            <div className="mb-3">
                              <Label
                                htmlFor="phoneNumber"
                                className="form-label"
                              >
                                {t("phoneNumber")}
                              </Label>
                              <span className="text-danger">*</span>
                              <Input
                                autoComplete="off"
                                id="phoneNumber"
                                name="phoneNumber"
                                className="form-control"
                                placeholder={t("enterPhoneNumber")}
                                type="text"
                                onBlur={form.handleBlur}
                                value={form.values.phoneNumber || ""}
                                invalid={
                                  form.touched.phoneNumber &&
                                  form.errors.phoneNumber
                                    ? true
                                    : false
                                }
                                maxLength={12}
                                onChange={(e) => {
                                  e.preventDefault();
                                  const { value } = e.target;
                                  const regex =
                                    /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                                  form.setFieldValue(
                                    "phoneNumber",
                                    regex.test(value.toString()) ? value : "",
                                  );
                                }}
                              />
                              {form.touched.phoneNumber &&
                              form.errors.phoneNumber ? (
                                <FormFeedback type="invalid">
                                  {form.errors.phoneNumber as string}
                                </FormFeedback>
                              ) : null}
                            </div>

                            <div className="mb-3">
                              <div className="float-end">
                                <Link
                                  tabIndex={-1}
                                  to="/forget-password"
                                  className="text-muted"
                                >
                                  {t("forgetPassword?")}
                                </Link>
                              </div>
                              <Label
                                className="form-label"
                                htmlFor="password-input"
                              >
                                {t("password")}
                              </Label>
                              <span className="text-danger">*</span>
                              <div className="position-relative auth-pass-inputgroup mb-3">
                                <Input
                                  id="password-input"
                                  name="password"
                                  value={form.values.password || ""}
                                  type={passwordType}
                                  className="form-control"
                                  placeholder={t("enterPassword")}
                                  onChange={form.handleChange}
                                  onBlur={form.handleBlur}
                                  invalid={
                                    form.touched.password &&
                                    form.errors.password
                                      ? true
                                      : false
                                  }
                                  maxLength={25}
                                />
                                {form.touched.password &&
                                form.errors.password ? (
                                  <FormFeedback type="invalid">
                                    {form.errors.password as string}
                                  </FormFeedback>
                                ) : null}
                                <button
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  id="password-addon"
                                >
                                  <i
                                    className="ri-eye-fill align-middle"
                                    onClick={handleClickShowPassword}
                                  ></i>
                                </button>
                              </div>
                            </div>
                            <div className="mt-4">
                              <ActivityButton
                                title="login"
                                inProgressTitle="loggingIn"
                                color="success"
                                className="btn btn-success w-100"
                                type="submit"
                                inProgress={isLoadingSignin}
                                disabled={isLoadingSignin}
                              ></ActivityButton>
                            </div>
                          </Form>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export const LoginPage = withRouter(LoginPageComponent);
